/**
 * Handles hashchange event. 
 * Determines which controller to load, based on the hash value
 * Parses data out of the hash (querystring format)
 * default route: #{Controller}/{Action}/{ExtraData}
 * Note everything after "#" is client-side and is not available on the server-side
 */
define('controllerManager',['require','exports','module','utils','controllers','services/googleanalytics','layout'],function (require, exports, module) {
    'use strict';
    var utils = require('utils');
    var controllers = require('controllers'); //TODO: consider during dev, load in the controllers dyanamcially. But make a chunk for the prod script called controllers to combine it in with the final output. So instead of requiring the controllers file here, do it in the build process.
    var googleanalytics = require('services/googleanalytics');
    var layout = require('layout');
    return {
        /**
         * Init the current controller if any
         * Single Page Route:
         *   #/{Controller}/{Action}/{Data}, where the controller name matches the js file name and Action and Data are both optional and can contain anything really
         *      Note we have the leading slash, so we don't match on normal anchor hashes, like /FAQ/#HotelArrivalFAQ
         *      Note that hash controllers can contain hyphens that will simply be removed to find the controller. e.g.: /#some-controller > somecontroller.js.load
         * https://regex101.com/r/sW8sY6/2
         * 
         * we will steadily work toward the server not sending controllers or controller-Data. instead fetch the data from the client-side. 
         * Basically the controllers' feData will be requested when the controller is requested. And when they login, send back the 
         * "ServerData.user" information...
         */
        init: function () {
            this.controllers = controllers;
            this.state = {};
            this.hashRegex = /^#\/([^\/\n]*)\/?([^\/\n]*)\/?(.*)$/;
            this.homeRegex = /^[#\s]?\/?$/;
            this.staticRegex = /^#\/static(\/.*)\.html.*$/;
            this.fileNameRegex = /([^\/\.]+)\.html/;
            this._load404Handler = this._load404.bind(this);
            layout.construct();
            $(window).on('hashchange', this._onHashChange.bind(this));
            var controller = this._getControllerFromHash();
            this._initCurrentController(controller);
            return this;
        },

        /**
         * Initiates the controllers that the server has indicated to initiate
         */
        _initCurrentController: function () {
            if (window.ServerData != null && window.ServerData.jsControllers.length > 0) {
                //old pipeline
                layout.setLayout();
                layout.init();
                console.log('Initiating:\n' + window.ServerData.jsControllers.join('\n'));
                for (var i = 0; i < window.ServerData.jsControllers.length; i++) {
                    this._initController(window.ServerData.jsControllers[i]);
                }
            } else {
                //new pipeline (SPA). TODO: consider by default await serverdata and dom load, unless the controller indicates to not wait and handles awaiting itself if need be (would only potentially speed up initial load...not a huge deal)
                layout.loadServerData().then(function () {
                    $(function () {
                        layout.init();
                        this._onHashChange(); //call hash change handler upon load to parse out where we are on the initial load.
                    }.bind(this));
                }.bind(this));
            }
        },

        /**
         * initiates the given controller (Legacy--once we get everything into single-page mode, this can go away...)
         */
        _initController: function (controllerName) {
            var controller = this._validateController(controllerName);
            controller.load != null ? controller.load() : controller();  // jshint ignore:line
            return controller;
        },

        /**
         * When the hash changes, think of this as a page load, but not. So do everything you'd do on a page load client-side
         * Note refresh layout everytime hash changes (note that this method is throttled though, so never will there be multiple getserverdata requests pending)
         * Note we close all fancyboxes when hash changes
         */
        _onHashChange: function () {
            var hash = window.location.hash;
            $.fancybox.close();
            layout.unsetBreadCrumbs();
            if (this._loadStatic(hash)) {
                return;
            }
            var controller = this._getControllerFromHash(hash);
            this._loadController(controller);
            layout.loadServerData();
        },

        /**
         * Check if hash is a static file and load it
         * Load 404 if file fails
         * @param {string} hash 
         * @returns {bool} 
         */
        _loadStatic: function(hash) {
            var filePath = hash.match(this.staticRegex);
            if (filePath == null || filePath[1] == null) {
                return false;
            }
            layout.initStatic(filePath[1]).then(function() {
                var fileName = hash.match(this.fileNameRegex);
                if (fileName == null) {
                    throw new Error('Your static file must end in ".html"');
                }
                document.title = this.getCleanTitle(fileName[1]);
            }.bind(this), this._load404Handler);
            return true;
        },

        /**
         * get a nice title based on a controller name or file name
         * @param {string} componentName 
         * @returns {string} 
         */
        getCleanTitle: function (componentName) {
            return componentName.replace(/[-_]/g, ' ').toTitleCase();
        },

        /**
         * Note we put the controller load on the next event loop, so the loading gif is shown between controllers
         * Note that controller.unload should:
         *      -kill event handlers specific to the controller
         *      -ractive instances should call teardown
         *      -jquery plugins should be destroyed
         *      -abort pending ajax requests (especially if the page waits for that to load before populating the layout-content div. Otherwise the old controller could overwrite a freshly-loaded controller)
         * @param {} controller 
         * @returns {} 
         */
        _loadController: function(controller) {
            if (controller == null) {
                return;
            }
            var title = this.getCleanTitle(controller.nameurl);
            document.title = title;
            googleanalytics.logPageView(controller.hash, title);
            //clean up old controller
            if (this.currentController != null) {
                this._stopControllerContentPopulation(this.currentController.instance);
                try {
                    this.currentController.instance.unload(); //wrap this in try catch in case call backs relied on the previous controller existing.
                } catch (e) {
                    
                }
            }
            this.currentController = controller;
            layout.setLoading();
            controller.instance.load(controller.action, controller.data, controller.state);
            window.scroll(0, 0);
            this._garbageCollectState();
        },

        /**
         * if your controller has a data load step prior to populating layout-content div, be sure to set it's "loading" prop to the jquery xhr, so it is aborted here
         */
        _stopControllerContentPopulation: function(instance) {
            if (utils.isFunc(utils.tryGetValue(instance, 'loading.abort'))) {
                instance.loading.abort();
                instance.loading = null;
            }
        },

        /**
         * Parse the controller data out of the hash
         * todo: route pattern for callcenter pages: /#/callcenter-membersearch. So find the "callcenter-membersearch" controller. Break up the controllers into folders, so controllers/callcenter/membersearch --> 'callcenter-membersearch' in the controller.js dictionary
         * @returns {Controller} 
         */
        _getControllerFromHash: function(hash) {
            hash = this.homeRegex.test(hash) ? '#/home' : hash; //if empty or "#" or "#/", go home
            if (!this.hashRegex.test(hash)) {
                return null;
            }
            var matches = hash.match(this.hashRegex);
            var nameurl = matches[1];
            var name = nameurl.replace(/-/g, ''); //if you need to use a controller that doesn't match at all? e.g. /#/easy --> /#/about -- define a way to make controller aliases = { easy: 'about' } and check that if nothing found that directly matches a controller name
            var controller = this._validateController(name);
            if (controller == null) {
                this._load404();
                return null;
            }
            return {
                hash: hash,
                name: name,
                action: matches[2],
                data: matches[3],
                state: this._getControllerState(name),
                instance: controller,
                nameurl: nameurl
            };
        },

        _load404: function() {
            var name = '404';
            this._loadController({
                hash: '#/404',
                name: name,
                action: '',
                data: '',
                state: this._getControllerState(name),
                instance: this._validateController(name),
                nameurl: name
            });
        },

        /**
         * Validates that the controller's file exists. If none is found null is returned
         * @param {string} controllerName 
         * @returns {Controller} 
         */
        _validateController: function (controllerName) {
            controllerName = controllerName.toLowerCase();
            var controllerComponents = this.controllers[controllerName];
            if (controllerComponents == null) {
                console.error('Did not find controller "{0}".\n\tBe sure to register it in controllers.js or run gulp js-controllers to rebuild controllers.js.'.format(controllerName)); // jshint ignore:line
                return null;
            }
            var controller = controllerComponents[0];
            if (!utils.isFunc(controller) && !utils.isFunc(controller.load)) {
                console.error('Controller "{0}" must be a function or an object with load/unload funcs.'.format(controllerName));
                return null;
            }
            return controller;
        },

        /**
         * Dictionary for storing controller state for quick loading. This is 
         * @param {string} controllerName 
         */
        _getControllerState: function(controllerName) {
            this.state[controllerName] = this.state[controllerName] || {};
            return this.state[controllerName];
        },

        /**
         * Store state for the last 5 controllers. The number is arbitrary, so change it as needed. At least store the last controller's state for pages that redirect to themselves with different hash (dest search)
         * Tip: if you store your whole controller in its state, with all your dom members set on it, you don't need to re-query the dom!
         * Note that this is set up to be called periodically: it deletes non-empty state objects that aren't in the last few to be set
         */
        _garbageCollectState: function() {
            var historyToKeep = 5;
            var historyCount = 0;
            Object.keys(this.state).reverse().forEach(function(controller) {
                if (this.state.hasOwnProperty(controller) && !$.isEmptyObject(this.state[controller])) {
                    historyCount++;
                    if (historyCount > historyToKeep) {
                        delete this.state[controller]; //todo: iterate over each property and inner array/obj, clearing all internal primitive values. then finally delete the entire prop...
                    }
                }
            }.bind(this));
        }
    };
});
